<template>
  <div class="p-2 sm:p-4 text-lexend overflow-x-hidden min-h-screen bg-gray-50 dark:bg-gray-900/50">
    <!-- Header Section -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-3 sm:mb-5 mt-1">
      <div class="animate-fadeIn w-full sm:w-auto">
        <div class="flex items-center gap-2 mb-1">
          <div class="w-8 h-1 bg-indigo-600 rounded-full"></div>
          <span class="text-[10px] sm:text-xs font-black text-indigo-600 tracking-widest">Panel de Control</span>
        </div>
        <h1 class="text-xl sm:text-3xl font-black text-gray-900 dark:text-white tracking-tight flex items-center flex-wrap gap-2">
          Gestión de Kardex
          <span class="px-2 sm:px-3 py-0.5 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 text-xs sm:text-sm font-black rounded-lg border border-indigo-200 dark:border-indigo-800">
            {{ herramientasConKardex.length }}
          </span>
        </h1>
      </div>
      <button 
        @click="openCreateModal"
        class="w-full sm:w-auto px-3 sm:px-5 py-2 sm:py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-black text-[10px] sm:text-xs tracking-widest transition-all shadow-md flex items-center justify-center gap-2"
      >
        <Icon icon="mdi:plus-circle" class="text-base sm:text-lg" />
        <span class="hidden sm:inline">Nuevo Kardex</span>
        <span class="sm:hidden">Nuevo</span>
      </button>
    </div>

    <!-- Tabs: Kardex | Movimientos -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-1 mb-3 sm:mb-5 flex gap-1 animate-fadeInUp">
      <button 
        @click="tabActivo = 'herramientas'"
        :class="['flex-1 px-3 sm:px-5 py-2 sm:py-2.5 rounded-xl font-black text-[10px] sm:text-xs tracking-widest transition-all flex items-center justify-center gap-1 sm:gap-2', 
          tabActivo === 'herramientas' 
            ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/30' 
            : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700']"
      >
        <Icon icon="mdi:tools" class="text-base sm:text-lg" />
        <span>Kardex</span>
        <span class="text-[8px] sm:text-[10px] bg-white/20 px-1.5 sm:px-2 py-0.5 rounded-full">{{ herramientasConKardex.length }}</span>
      </button>
      <button 
        @click="tabActivo = 'kardex'"
        :class="['flex-1 px-3 sm:px-5 py-2 sm:py-2.5 rounded-xl font-black text-[10px] sm:text-xs tracking-widest transition-all flex items-center justify-center gap-1 sm:gap-2',
          tabActivo === 'kardex' 
            ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-500/30' 
            : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700']"
      >
        <Icon icon="mdi:history" class="text-base sm:text-lg" />
        <span>Movimientos</span>
        <span class="text-[8px] sm:text-[10px] bg-white/20 px-1.5 sm:px-2 py-0.5 rounded-full">{{ kardex.length }}</span>
      </button>
    </div>

    <!-- ============================================ -->
    <!-- TABLA DE HERRAMIENTAS CON KARDEX -->
    <!-- ============================================ -->
    <div v-if="tabActivo === 'herramientas'">
      <!-- Filtros -->
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-5 mb-3 sm:mb-5 animate-fadeInUp delay-100">
        <div class="grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-5 items-end">
          <div class="sm:col-span-7 lg:col-span-8">
            <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1">Búsqueda Inteligente</label>
            <div class="relative group">
              <Icon icon="mdi:magnify" class="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg sm:text-2xl group-focus-within:text-indigo-500 transition-colors" />
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Buscar por código, nombre o sede..."
                class="w-full pl-10 sm:pl-14 pr-3 sm:pr-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 focus:bg-white dark:focus:bg-gray-700 rounded-xl transition-all duration-300 outline-none dark:text-white font-bold text-sm sm:text-base"
              />
            </div>
          </div>
          
          <div class="sm:col-span-3 lg:col-span-2">
            <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1">Estado</label>
            <select
              v-model="filtroEstado"
              class="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 rounded-xl font-black text-gray-800 dark:text-white outline-none transition-all appearance-none cursor-pointer text-sm sm:text-base"
            >
              <option value="">TODOS</option>
              <option value="Activo">ACTIVO</option>
              <option value="Mantenimiento">MANTENIMIENTO</option>
              <option value="Inactivo">INACTIVO</option>
            </select>
          </div>

          <div class="sm:col-span-2 lg:col-span-2">
            <button 
              @click="fetchHerramientasConKardex"
              class="w-full py-2.5 sm:py-3.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-xl font-black text-[10px] sm:text-xs tracking-widest hover:bg-gray-200 dark:hover:bg-gray-600 transition-all flex items-center justify-center gap-2 border border-gray-200 dark:border-gray-600 shadow-sm"
            >
              <Icon icon="mdi:refresh" :class="{'animate-spin': cargando}" class="text-lg sm:text-xl" />
              <span class="hidden sm:inline">Refrescar</span>
              <span class="sm:hidden">Refrescar</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Stats Cards -->
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-3 sm:mb-5 animate-fadeInUp delay-200">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4 text-center">
          <p class="text-[8px] sm:text-[10px] font-black text-gray-400 tracking-widest uppercase">Total</p>
          <p class="text-xl sm:text-2xl font-black text-gray-900 dark:text-white">{{ herramientasConKardex.length }}</p>
        </div>
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4 text-center">
          <p class="text-[8px] sm:text-[10px] font-black text-gray-400 tracking-widest uppercase">Activas</p>
          <p class="text-xl sm:text-2xl font-black text-emerald-600">{{ herramientasActivas }}</p>
        </div>
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4 text-center">
          <p class="text-[8px] sm:text-[10px] font-black text-gray-400 tracking-widest uppercase">Mantenimiento</p>
          <p class="text-xl sm:text-2xl font-black text-amber-600">{{ herramientasMantenimiento }}</p>
        </div>
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4 text-center">
          <p class="text-[8px] sm:text-[10px] font-black text-gray-400 tracking-widest uppercase">Stock Total</p>
          <p class="text-xl sm:text-2xl font-black text-indigo-600">{{ stockTotal }}</p>
        </div>
      </div>

      <!-- Data Table -->
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden animate-fadeInUp delay-300">
        <!-- Versión Desktop -->
        <div class="hidden lg:block overflow-x-auto custom-scrollbar">
          <table class="w-full text-left border-collapse">
            <thead>
              <tr class="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Código</th>
                <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Herramienta</th>
                <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Sede</th>
                <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Categoría</th>
                <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center hidden xl:table-cell">Estante</th>
                <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center hidden xl:table-cell">Pendaño</th>
                <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center">Stock</th>
                <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center">Estado</th>
                <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-right">Acciones</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
              <tr v-if="cargando" v-for="i in 5" :key="'load-'+i" class="animate-pulse">
                <td colspan="9" class="px-5 py-4"><div class="h-4 bg-gray-100 dark:bg-gray-700 rounded w-full"></div></td>
              </tr>
              <tr v-if="!cargando && filteredHerramientas.length === 0">
                <td colspan="9" class="px-5 py-16 text-center">
                  <div class="flex flex-col items-center opacity-30 select-none">
                    <Icon icon="mdi:tools-off" class="text-7xl mb-3" />
                    <h4 class="text-xl font-black text-gray-400 tracking-widest">No hay herramientas con Kardex</h4>
                    <p class="text-sm text-gray-300 mt-1">Registra tu primer Kardex</p>
                  </div>
                </td>
              </tr>
              <tr v-for="h in paginatedHerramientas" :key="h.idHerramienta" class="hover:bg-indigo-50/30 dark:hover:bg-gray-700/30 transition-all group">
                <td class="px-5 py-4">
                  <span class="px-2.5 py-1 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-lg font-black text-xs border border-indigo-100 dark:border-indigo-800 transition-transform inline-block">
                    {{ h.codigo }}
                  </span>
                </td>
                <td class="px-5 py-4">
                  <div class="text-left">
                    <p class="font-black text-gray-900 dark:text-white text-xs mb-1">{{ h.nombre }}</p>
                    <p class="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[200px]" :title="h.descripcion">
                      {{ h.descripcion || 'Sin descripción' }}
                    </p>
                  </div>
                </td>
                <td class="px-5 py-4">
                  <div class="flex items-center gap-2">
                    <Icon icon="mdi:store-marker" class="text-amber-500 text-base" />
                    <span class="font-black text-gray-700 dark:text-gray-300 text-xs">{{ h.nombreSede || '-' }}</span>
                  </div>
                </td>
                <td class="px-5 py-4">
                  <span class="text-xs font-bold text-gray-600 dark:text-gray-400">{{ h.nombreCategoria || '-' }}</span>
                </td>
                <td class="px-5 py-4 text-center hidden xl:table-cell">
                  <span class="text-xs font-bold text-gray-600 dark:text-gray-400">{{ h.estante || '-' }}</span>
                </td>
                <td class="px-5 py-4 text-center hidden xl:table-cell">
                  <span class="text-xs font-bold text-gray-600 dark:text-gray-400">{{ h.pendano || '-' }}</span>
                </td>
                <td class="px-5 py-4 text-center">
                  <span :class="['text-sm font-black px-3 py-1 rounded-lg', h.stock > 0 ? 'text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-700' : 'text-rose-500 bg-rose-50 dark:bg-rose-900/30']">
                    {{ h.stock }}
                  </span>
                </td>
                <td class="px-5 py-4 text-center">
                  <span :class="[
                    'px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest border shadow-sm inline-block',
                    h.estado === 'Activo' ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800' :
                    h.estado === 'Mantenimiento' ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800' :
                    'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800'
                  ]">
                    {{ h.estado }}
                  </span>
                </td>
                <td class="px-5 py-4 text-right">
                  <div class="flex items-center justify-end gap-1 flex-nowrap">
                    <button @click="openEditModal(h)" title="Editar" class="p-1.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-600 hover:text-white transition-all shadow-sm">
                      <Icon icon="mdi:pencil" class="text-base" />
                    </button>
                    <button @click="openInfoModal(h)" title="Ver información" class="p-1.5 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-lg hover:bg-amber-600 hover:text-white transition-all shadow-sm">
                      <Icon icon="mdi:information-outline" class="text-base" />
                    </button>
                    <button @click="confirmDeleteHerramienta(h)" title="Eliminar" class="p-1.5 bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-lg hover:bg-rose-600 hover:text-white transition-all shadow-sm">
                      <Icon icon="mdi:delete" class="text-base" />
                    </button>
                    <button @click="verKardex(h)" title="Ver Movimientos" class="p-1.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-lg hover:bg-emerald-600 hover:text-white transition-all shadow-sm">
                      <Icon icon="mdi:history" class="text-base" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Versión Mobile -->
        <div class="lg:hidden space-y-3 p-2">
          <div v-if="cargando" v-for="i in 3" :key="'load-mobile-'+i" class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 animate-pulse">
            <div class="h-4 bg-gray-100 dark:bg-gray-700 rounded w-full mb-2"></div>
            <div class="h-3 bg-gray-100 dark:bg-gray-700 rounded w-3/4"></div>
          </div>
          
          <div v-if="!cargando && filteredHerramientas.length === 0" class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
            <div class="flex flex-col items-center opacity-30 select-none">
              <Icon icon="mdi:tools-off" class="text-6xl mb-3" />
              <h4 class="text-lg font-black text-gray-400 tracking-widest">No hay herramientas</h4>
            </div>
          </div>
          
          <div v-for="h in paginatedHerramientas" :key="h.idHerramienta" 
               class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 p-4 hover:shadow-lg transition-all duration-200">
            
            <div class="flex flex-wrap items-center justify-between gap-2 mb-3 pb-2 border-b border-gray-100 dark:border-gray-700">
              <div class="flex items-center gap-2">
                <span class="px-2.5 py-1 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-lg font-black text-xs border border-indigo-100 dark:border-indigo-800">
                  {{ h.codigo }}
                </span>
                <span :class="[
                  'px-2 py-0.5 rounded-lg text-[8px] font-black uppercase tracking-widest border shadow-sm',
                  h.estado === 'Activo' ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800' :
                  h.estado === 'Mantenimiento' ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800' :
                  'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800'
                ]">
                  {{ h.estado }}
                </span>
              </div>
              <div class="text-xs font-black text-gray-700 dark:text-gray-300">
                <span class="text-gray-400 font-normal">Stock:</span> {{ h.stock }}
              </div>
            </div>

            <div class="mb-2">
              <p class="font-black text-gray-900 dark:text-white text-sm">{{ h.nombre }}</p>
              <p class="text-[10px] text-gray-500 dark:text-gray-400 italic truncate">{{ h.descripcion || 'Sin descripción' }}</p>
            </div>

            <div class="grid grid-cols-2 gap-1 mb-2 text-[10px]">
              <div class="flex items-center gap-1">
                <Icon icon="mdi:store-marker" class="text-amber-500 text-sm" />
                <span class="font-bold text-gray-600 dark:text-gray-400 truncate">{{ h.nombreSede || '-' }}</span>
              </div>
              <div class="flex items-center gap-1">
                <Icon icon="mdi:tag" class="text-indigo-500 text-sm" />
                <span class="font-bold text-gray-600 dark:text-gray-400 truncate">{{ h.nombreCategoria || '-' }}</span>
              </div>
            </div>

            <div v-if="h.estante || h.pendano" class="grid grid-cols-2 gap-1 mb-3 text-[10px] bg-gray-50 dark:bg-gray-900/30 rounded-xl p-2">
              <div class="flex items-center gap-1">
                <Icon icon="mdi:shelf" class="text-emerald-500 text-sm" />
                <span class="font-bold text-gray-600 dark:text-gray-400">{{ h.estante || 'N/A' }}</span>
              </div>
              <div class="flex items-center gap-1">
                <Icon icon="mdi:map-marker-path" class="text-emerald-500 text-sm" />
                <span class="font-bold text-gray-600 dark:text-gray-400">{{ h.pendano || 'N/A' }}</span>
              </div>
            </div>

            <div class="flex flex-wrap items-center gap-1.5 pt-2 border-t border-gray-200 dark:border-gray-700">
              <button @click="openEditModal(h)" title="Editar" class="p-2 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-600 hover:text-white transition-all shadow-sm flex-1 sm:flex-none flex items-center justify-center gap-1">
                <Icon icon="mdi:pencil" class="text-sm" />
                <span class="text-[8px] font-black sm:hidden">Editar</span>
              </button>
              <button @click="openInfoModal(h)" title="Ver información" class="p-2 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-lg hover:bg-amber-600 hover:text-white transition-all shadow-sm flex-1 sm:flex-none flex items-center justify-center gap-1">
                <Icon icon="mdi:information-outline" class="text-sm" />
                <span class="text-[8px] font-black sm:hidden">Info</span>
              </button>
              <button @click="confirmDeleteHerramienta(h)" title="Eliminar" class="p-2 bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-lg hover:bg-rose-600 hover:text-white transition-all shadow-sm flex-1 sm:flex-none flex items-center justify-center gap-1">
                <Icon icon="mdi:delete" class="text-sm" />
                <span class="text-[8px] font-black sm:hidden">Eliminar</span>
              </button>
              <button @click="verKardex(h)" title="Ver Movimientos" class="p-2 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-lg hover:bg-emerald-600 hover:text-white transition-all shadow-sm flex-1 sm:flex-none flex items-center justify-center gap-1">
                <Icon icon="mdi:history" class="text-sm" />
                <span class="text-[8px] font-black sm:hidden">Kardex</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="filteredHerramientas.length > 0" class="bg-gray-50/50 dark:bg-gray-800/50 px-3 sm:px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div class="text-[10px] sm:text-[11px] font-black text-gray-400 uppercase tracking-widest text-center sm:text-left">
            MOSTRANDO {{ (currentPage - 1) * itemsPerPage + 1 }} - {{ Math.min(currentPage * itemsPerPage, filteredHerramientas.length) }} DE {{ filteredHerramientas.length }}
          </div>
          <div class="flex items-center gap-1 flex-wrap justify-center">
            <button @click="goToFirstPage" :disabled="currentPage === 1" :class="['p-1.5 rounded-lg border transition-all hidden sm:inline-flex', currentPage === 1 ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
              <Icon icon="mdi:chevron-double-left" class="text-base sm:text-lg" />
            </button>
            <button @click="currentPage = Math.max(currentPage - 1, 1)" :disabled="currentPage === 1" :class="['p-1.5 rounded-lg border transition-all', currentPage === 1 ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
              <Icon icon="mdi:chevron-left" class="text-base sm:text-2xl" />
            </button>
            <div class="hidden sm:flex items-center gap-0.5 sm:gap-1 mx-1 sm:mx-2">
              <button v-for="page in displayedPages" :key="page" @click="currentPage = page" :class="['w-7 h-7 sm:w-9 sm:h-9 rounded-lg text-[10px] sm:text-xs font-black transition-all flex items-center justify-center', currentPage === page ? 'bg-indigo-600 text-white shadow-md shadow-indigo-500/30' : 'text-gray-500 hover:bg-white dark:hover:bg-gray-700 border border-transparent hover:border-gray-200 dark:hover:border-gray-600']">
                {{ page }}
              </button>
            </div>
            <span class="sm:hidden text-xs font-black text-gray-600 dark:text-gray-300 px-2">{{ currentPage }} / {{ totalPages }}</span>
            <button @click="currentPage = Math.min(currentPage + 1, totalPages)" :disabled="currentPage === totalPages" :class="['p-1.5 rounded-lg border transition-all', currentPage === totalPages ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
              <Icon icon="mdi:chevron-right" class="text-base sm:text-2xl" />
            </button>
            <button @click="goToLastPage" :disabled="currentPage === totalPages" :class="['p-1.5 rounded-lg border transition-all hidden sm:inline-flex', currentPage === totalPages ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
              <Icon icon="mdi:chevron-double-right" class="text-base sm:text-lg" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ============================================ -->
    <!-- TABLA DE MOVIMIENTOS -->
    <!-- ============================================ -->
    <div v-if="tabActivo === 'kardex'">
      <!-- Filtros Movimientos -->
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-5 mb-3 sm:mb-5 animate-fadeInUp delay-100">
        <div class="grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-5 items-end">
          <div class="sm:col-span-5 lg:col-span-6">
            <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1">Búsqueda</label>
            <div class="relative group">
              <Icon icon="mdi:magnify" class="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg sm:text-2xl group-focus-within:text-emerald-500 transition-colors" />
              <input
                v-model="kardexSearchQuery"
                type="text"
                placeholder="Buscar por herramienta o motivo..."
                class="w-full pl-10 sm:pl-14 pr-3 sm:pr-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-emerald-500 focus:bg-white dark:focus:bg-gray-700 rounded-xl transition-all duration-300 outline-none dark:text-white font-bold text-sm sm:text-base"
              />
            </div>
          </div>
          
          <div class="sm:col-span-4 lg:col-span-3">
            <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1">Tipo Movimiento</label>
            <select
              v-model="kardexFilterTipo"
              class="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-emerald-500 rounded-xl font-black text-gray-800 dark:text-white outline-none transition-all appearance-none cursor-pointer text-sm sm:text-base"
            >
              <option value="all">TODOS</option>
              <option value="entrada">ENTRADA</option>
              <option value="salida">SALIDA</option>
              <option value="ajuste">AJUSTE</option>
              <option value="devolucion">DEVOLUCIÓN</option>
            </select>
          </div>

          <div class="sm:col-span-3 lg:col-span-3 flex gap-2">
            <button @click="resetKardexFilters" class="flex-1 py-2.5 sm:py-3.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-xl font-black text-[10px] sm:text-xs tracking-widest hover:bg-gray-200 dark:hover:bg-gray-600 transition-all flex items-center justify-center gap-2 border border-gray-200 dark:border-gray-600 shadow-sm">
              <Icon icon="mdi:filter-off" class="text-lg sm:text-xl" />
              <span class="hidden sm:inline">Limpiar</span>
            </button>
            <button @click="fetchKardexData" class="flex-1 py-2.5 sm:py-3.5 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 rounded-xl font-black text-[10px] sm:text-xs tracking-widest hover:bg-emerald-100 transition-all flex items-center justify-center gap-2 border border-emerald-200 dark:border-emerald-800 shadow-sm">
              <Icon icon="mdi:refresh" :class="{'animate-spin': cargandoKardex}" class="text-lg sm:text-xl" />
              <span class="hidden sm:inline">Actualizar</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Stats Movimientos -->
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-3 sm:mb-5 animate-fadeInUp delay-200">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4 text-center">
          <p class="text-[8px] sm:text-[10px] font-black text-gray-400 tracking-widest uppercase">Total</p>
          <p class="text-xl sm:text-2xl font-black text-gray-900 dark:text-white">{{ kardex.length }}</p>
        </div>
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4 text-center">
          <p class="text-[8px] sm:text-[10px] font-black text-gray-400 tracking-widest uppercase">Entradas</p>
          <p class="text-xl sm:text-2xl font-black text-emerald-600">{{ totalKardexEntradas }}</p>
        </div>
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4 text-center">
          <p class="text-[8px] sm:text-[10px] font-black text-gray-400 tracking-widest uppercase">Salidas</p>
          <p class="text-xl sm:text-2xl font-black text-rose-600">{{ totalKardexSalidas }}</p>
        </div>
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4 text-center">
          <p class="text-[8px] sm:text-[10px] font-black text-gray-400 tracking-widest uppercase">Herramientas</p>
          <p class="text-xl sm:text-2xl font-black text-indigo-600">{{ herramientasUnicasKardex }}</p>
        </div>
      </div>

      <!-- Data Table Movimientos -->
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden animate-fadeInUp delay-300">
        <!-- Versión Desktop -->
        <div class="hidden lg:block overflow-x-auto custom-scrollbar">
          <table class="w-full text-left border-collapse">
            <thead>
              <tr class="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">#</th>
                <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Herramienta</th>
                <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center">Tipo</th>
                <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center">Cantidad</th>
                <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center">Stock</th>
                <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Motivo</th>
                <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest hidden xl:table-cell">Sede</th>
                <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Fecha</th>
                <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-right">Acciones</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
              <tr v-if="cargandoKardex" v-for="i in 5" :key="'load-kardex-'+i" class="animate-pulse">
                <td colspan="9" class="px-5 py-4"><div class="h-4 bg-gray-100 dark:bg-gray-700 rounded w-full"></div></td>
              </tr>
              <tr v-if="!cargandoKardex && filteredKardex.length === 0">
                <td colspan="9" class="px-5 py-16 text-center">
                  <div class="flex flex-col items-center opacity-30 select-none">
                    <Icon icon="mdi:history-off" class="text-7xl mb-3" />
                    <h4 class="text-xl font-black text-gray-400 tracking-widest">No hay movimientos</h4>
                    <p class="text-sm text-gray-300 mt-1">Los movimientos aparecerán aquí</p>
                  </div>
                </td>
              </tr>
              <tr v-for="(item, index) in paginatedKardex" :key="item.idKardex" class="hover:bg-emerald-50/30 dark:hover:bg-gray-700/30 transition-all group">
                <td class="px-5 py-4">
                  <span class="text-xs font-mono font-bold text-gray-400">{{ (kardexCurrentPage - 1) * itemsPerPage + index + 1 }}</span>
                </td>
                <td class="px-5 py-4">
                  <div class="text-left">
                    <p class="font-black text-gray-900 dark:text-white text-xs">{{ item.nombreHerramienta || 'Sin nombre' }}</p>
                    <p class="text-[10px] text-gray-500 dark:text-gray-400">{{ item.codigoHerramienta || 'N/A' }}</p>
                  </div>
                </td>
                <td class="px-5 py-4 text-center">
                  <span :class="['px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest border shadow-sm inline-block', getTipoKardexClass(item.tipoMovimiento)]">
                    {{ item.tipoMovimiento }}
                  </span>
                </td>
                <td class="px-5 py-4 text-center">
                  <span :class="['text-sm font-black', getCantidadKardexClass(item.tipoMovimiento)]">
                    {{ item.cantidad }}
                  </span>
                </td>
                <td class="px-5 py-4 text-center">
                  <span class="text-sm font-black text-gray-900 dark:text-white">{{ item.stockNuevo }}</span>
                </td>
                <td class="px-5 py-4">
                  <span class="text-xs text-gray-600 dark:text-gray-300 truncate max-w-[200px] block">{{ item.motivo }}</span>
                </td>
                <td class="px-5 py-4 hidden xl:table-cell">
                  <span class="text-xs font-bold text-gray-600 dark:text-gray-400">{{ item.nombreSede || '-' }}</span>
                </td>
                <td class="px-5 py-4">
                  <div class="text-xs font-bold text-gray-600 dark:text-gray-300">{{ formatDate(item.fechaMovimiento) }}</div>
                </td>
                <td class="px-5 py-4 text-right">
                  <div class="flex items-center justify-end gap-1">
                    <button @click="confirmDeleteKardex(item)" title="Eliminar movimiento" class="p-1.5 bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-lg hover:bg-rose-600 hover:text-white transition-all shadow-sm">
                      <Icon icon="mdi:delete" class="text-base" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Versión Mobile Movimientos -->
        <div class="lg:hidden space-y-3 p-2">
          <div v-if="cargandoKardex" v-for="i in 3" :key="'load-mobile-kardex-'+i" class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 animate-pulse">
            <div class="h-4 bg-gray-100 dark:bg-gray-700 rounded w-full mb-2"></div>
            <div class="h-3 bg-gray-100 dark:bg-gray-700 rounded w-3/4"></div>
          </div>
          
          <div v-if="!cargandoKardex && filteredKardex.length === 0" class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
            <div class="flex flex-col items-center opacity-30 select-none">
              <Icon icon="mdi:history-off" class="text-6xl mb-3" />
              <h4 class="text-lg font-black text-gray-400 tracking-widest">No hay movimientos</h4>
            </div>
          </div>
          
          <div v-for="(item, index) in paginatedKardex" :key="item.idKardex" 
               class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 p-4 hover:shadow-lg transition-all duration-200">
            
            <div class="flex flex-wrap items-center justify-between gap-2 mb-3 pb-2 border-b border-gray-100 dark:border-gray-700">
              <div class="flex items-center gap-2">
                <span class="text-[10px] font-mono font-bold text-gray-400">#{{ index + 1 }}</span>
                <span :class="['px-2 py-0.5 rounded-lg text-[8px] font-black uppercase tracking-widest border shadow-sm', getTipoKardexClass(item.tipoMovimiento)]">
                  {{ item.tipoMovimiento }}
                </span>
              </div>
              <div class="text-xs font-black">
                <span :class="['px-2 py-0.5 rounded-lg', getCantidadKardexClass(item.tipoMovimiento)]">
                  {{ item.tipoMovimiento === 'entrada' || item.tipoMovimiento === 'devolucion' ? '+' : '-' }}{{ item.cantidad }}
                </span>
              </div>
            </div>

            <div class="mb-2">
              <p class="font-black text-gray-900 dark:text-white text-sm">{{ item.nombreHerramienta || 'Sin nombre' }}</p>
              <p class="text-[10px] text-gray-500 dark:text-gray-400">{{ item.codigoHerramienta || 'N/A' }}</p>
            </div>

            <div class="grid grid-cols-2 gap-1 mb-2 text-[10px] bg-gray-50 dark:bg-gray-900/30 rounded-xl p-2">
              <div>
                <span class="text-gray-400 font-black">Stock:</span>
                <span class="font-bold text-gray-700 dark:text-gray-300 ml-1">{{ item.stockNuevo }}</span>
              </div>
              <div>
                <span class="text-gray-400 font-black">Fecha:</span>
                <span class="font-bold text-gray-700 dark:text-gray-300 ml-1">{{ formatDate(item.fechaMovimiento) }}</span>
              </div>
            </div>

            <div class="mb-2">
              <p class="text-xs text-gray-600 dark:text-gray-400">{{ item.motivo }}</p>
            </div>

            <div class="flex flex-wrap items-center gap-1.5 pt-2 border-t border-gray-200 dark:border-gray-700">
              <button @click="confirmDeleteKardex(item)" title="Eliminar" class="flex-1 p-2 bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-lg hover:bg-rose-600 hover:text-white transition-all shadow-sm flex items-center justify-center gap-1">
                <Icon icon="mdi:delete" class="text-sm" />
                <span class="text-[8px] font-black">Eliminar</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Pagination Kardex -->
        <div v-if="filteredKardex.length > 0" class="bg-gray-50/50 dark:bg-gray-800/50 px-3 sm:px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div class="text-[10px] sm:text-[11px] font-black text-gray-400 uppercase tracking-widest text-center sm:text-left">
            MOSTRANDO {{ (kardexCurrentPage - 1) * itemsPerPage + 1 }} - {{ Math.min(kardexCurrentPage * itemsPerPage, filteredKardex.length) }} DE {{ filteredKardex.length }}
          </div>
          <div class="flex items-center gap-1 flex-wrap justify-center">
            <button @click="kardexCurrentPage = 1" :disabled="kardexCurrentPage === 1" :class="['p-1.5 rounded-lg border transition-all hidden sm:inline-flex', kardexCurrentPage === 1 ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
              <Icon icon="mdi:chevron-double-left" class="text-base sm:text-lg" />
            </button>
            <button @click="kardexCurrentPage = Math.max(kardexCurrentPage - 1, 1)" :disabled="kardexCurrentPage === 1" :class="['p-1.5 rounded-lg border transition-all', kardexCurrentPage === 1 ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
              <Icon icon="mdi:chevron-left" class="text-base sm:text-2xl" />
            </button>
            <div class="hidden sm:flex items-center gap-0.5 sm:gap-1 mx-1 sm:mx-2">
              <button v-for="page in kardexDisplayedPages" :key="page" @click="kardexCurrentPage = page" :class="['w-7 h-7 sm:w-9 sm:h-9 rounded-lg text-[10px] sm:text-xs font-black transition-all flex items-center justify-center', kardexCurrentPage === page ? 'bg-emerald-600 text-white shadow-md shadow-emerald-500/30' : 'text-gray-500 hover:bg-white dark:hover:bg-gray-700 border border-transparent hover:border-gray-200 dark:hover:border-gray-600']">
                {{ page }}
              </button>
            </div>
            <span class="sm:hidden text-xs font-black text-gray-600 dark:text-gray-300 px-2">{{ kardexCurrentPage }} / {{ kardexTotalPages }}</span>
            <button @click="kardexCurrentPage = Math.min(kardexCurrentPage + 1, kardexTotalPages)" :disabled="kardexCurrentPage === kardexTotalPages" :class="['p-1.5 rounded-lg border transition-all', kardexCurrentPage === kardexTotalPages ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
              <Icon icon="mdi:chevron-right" class="text-base sm:text-2xl" />
            </button>
            <button @click="kardexCurrentPage = kardexTotalPages" :disabled="kardexCurrentPage === kardexTotalPages" :class="['p-1.5 rounded-lg border transition-all hidden sm:inline-flex', kardexCurrentPage === kardexTotalPages ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
              <Icon icon="mdi:chevron-double-right" class="text-base sm:text-lg" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ============================================ -->
    <!-- MODAL FORMULARIO -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="showModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9998]" @click.self="showModal = false">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden animate-zoomIn">
          <div class="bg-gradient-to-r from-indigo-600 to-purple-600 px-4 sm:px-6 py-4 flex items-center gap-3 sticky top-0 z-10">
            <div class="p-2 bg-white/20 rounded-xl">
              <Icon :icon="isEditing ? 'mdi:pencil' : 'mdi:plus-circle'" class="text-white text-xl" />
            </div>
            <div>
              <h3 class="text-sm font-black text-white">{{ isEditing ? 'Editar Kardex' : 'Nuevo Kardex' }}</h3>
              <p class="text-[10px] text-white/70 font-bold">{{ isEditing ? 'Modifica los datos' : 'Registra un nuevo Kardex' }}</p>
            </div>
            <button @click="showModal = false" class="ml-auto p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all">
              <Icon icon="mdi:close" class="text-white text-lg" />
            </button>
          </div>
          <div class="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
            <form @submit.prevent="saveHerramienta" class="space-y-5">
              <!-- DATOS DE LA HERRAMIENTA -->
              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                <h4 class="text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <Icon icon="mdi:tools" class="text-indigo-500" />
                  Datos de la Herramienta
                </h4>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">
                      <Icon icon="mdi:qrcode" class="inline mr-1" /> Código *
                    </label>
                    <input 
                      v-model="form.codigo" 
                      type="text" 
                      required 
                      class="w-full px-3 sm:px-4 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-indigo-500 rounded-xl text-xs font-medium text-gray-800 dark:text-white outline-none transition-all"
                      placeholder="Ej: HER-001"
                    />
                  </div>
                  <div>
                    <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">
                      <Icon icon="mdi:format-text" class="inline mr-1" /> Nombre *
                    </label>
                    <input 
                      v-model="form.nombre" 
                      type="text" 
                      required 
                      class="w-full px-3 sm:px-4 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-indigo-500 rounded-xl text-xs font-medium text-gray-800 dark:text-white outline-none transition-all"
                      placeholder="Nombre de la herramienta"
                    />
                  </div>
                </div>
                <div class="mt-3">
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">
                    <Icon icon="mdi:text-box" class="inline mr-1" /> Descripción *
                  </label>
                  <textarea 
                    v-model="form.descripcion" 
                    rows="2" 
                    required
                    class="w-full px-3 sm:px-4 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-indigo-500 rounded-xl text-xs font-medium text-gray-800 dark:text-white outline-none transition-all resize-none"
                    placeholder="Descripción detallada..."
                  />
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                  <div>
                    <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">
                      <Icon icon="mdi:store-marker" class="inline mr-1" /> Sede *
                    </label>
                    <select v-model="form.idSede" required class="w-full px-3 sm:px-4 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-indigo-500 rounded-xl text-xs font-black text-gray-800 dark:text-white outline-none transition-all appearance-none cursor-pointer">
                      <option value="">Seleccionar sede...</option>
                      <option v-for="sede in sedesList" :key="sede.idSede" :value="sede.idSede">
                        {{ sede.nombre }}
                      </option>
                    </select>
                  </div>
                  <div>
                    <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">
                      <Icon icon="mdi:tag" class="inline mr-1" /> Categoría *
                    </label>
                    <select v-model="form.idCategoria" required class="w-full px-3 sm:px-4 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-indigo-500 rounded-xl text-xs font-black text-gray-800 dark:text-white outline-none transition-all appearance-none cursor-pointer">
                      <option value="">Seleccionar categoría...</option>
                      <option v-for="cat in categoriasList" :key="cat.idCategoria" :value="cat.idCategoria">
                        {{ cat.nombre }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                  <div>
                    <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">
                      <Icon icon="mdi:tag-text" class="inline mr-1" /> Marca
                    </label>
                    <input 
                      v-model="form.marca" 
                      type="text" 
                      class="w-full px-3 sm:px-4 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-indigo-500 rounded-xl text-xs font-medium text-gray-800 dark:text-white outline-none transition-all"
                      placeholder="Ej: Bosch, DeWalt..."
                    />
                  </div>
                  <div>
                    <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">
                      <Icon icon="mdi:package" class="inline mr-1" /> Stock Inicial
                    </label>
                    <input 
                      v-model.number="form.stock" 
                      type="number" 
                      min="0"
                      class="w-full px-3 sm:px-4 py-2.5 bg-emerald-50 dark:bg-emerald-900/20 border-2 border-emerald-200 dark:border-emerald-800/30 focus:border-emerald-500 rounded-xl text-xs font-black text-emerald-700 dark:text-emerald-300 outline-none transition-all"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">
                      <Icon icon="mdi:state-machine" class="inline mr-1" /> Estado
                    </label>
                    <select v-model="form.estado" class="w-full px-3 sm:px-4 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-indigo-500 rounded-xl text-xs font-black text-gray-800 dark:text-white outline-none transition-all appearance-none cursor-pointer">
                      <option value="Activo">Activo</option>
                      <option value="Inactivo">Inactivo</option>
                      <option value="Mantenimiento">Mantenimiento</option>
                    </select>
                  </div>
                </div>
              </div>

              <!-- UBICACIÓN Y PROCEDENCIA -->
              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                <h4 class="text-[10px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <Icon icon="mdi:map-marker" class="text-emerald-500" />
                  Ubicación y Procedencia
                </h4>
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">
                      <Icon icon="mdi:shelf" class="inline mr-1" /> Estante
                    </label>
                    <input 
                      v-model="kardexForm.estante" 
                      type="text" 
                      class="w-full px-3 sm:px-4 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-emerald-500 rounded-xl text-xs font-medium text-gray-800 dark:text-white outline-none transition-all"
                      placeholder="Ej: A3, B2..."
                    />
                  </div>
                  <div>
                    <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">
                      <Icon icon="mdi:map-marker-path" class="inline mr-1" /> Pendaño
                    </label>
                    <input 
                      v-model="kardexForm.pendano" 
                      type="text" 
                      class="w-full px-3 sm:px-4 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-emerald-500 rounded-xl text-xs font-medium text-gray-800 dark:text-white outline-none transition-all"
                      placeholder="Pasillo 2, Fila 3..."
                    />
                  </div>
                  <div>
                    <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">
                      <Icon icon="mdi:account" class="inline mr-1" /> Dueño Anterior
                    </label>
                    <input 
                      v-model="kardexForm.dueñoAnterior" 
                      type="text" 
                      class="w-full px-3 sm:px-4 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-emerald-500 rounded-xl text-xs font-medium text-gray-800 dark:text-white outline-none transition-all"
                      placeholder="Nombre del dueño anterior..."
                    />
                  </div>
                </div>
              </div>

              <!-- INFORMACIÓN DEL EQUIPO -->
              <div class="bg-amber-50 dark:bg-amber-900/10 rounded-xl p-4 border border-amber-200 dark:border-amber-800/30">
                <h4 class="text-[10px] font-black text-amber-600 dark:text-amber-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <Icon icon="mdi:information" class="text-amber-500" />
                  Información del Equipo
                </h4>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">
                      <Icon icon="mdi:calendar" class="inline mr-1" /> Fecha de Ingreso
                    </label>
                    <input 
                      v-model="kardexForm.fechaIngresoManual" 
                      type="date" 
                      class="w-full px-3 sm:px-4 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-amber-500 rounded-xl text-xs font-medium text-gray-800 dark:text-white outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">
                      <Icon icon="mdi:phone" class="inline mr-1" /> Teléfono del Cliente
                    </label>
                    <input 
                      v-model="kardexForm.telefonoCliente" 
                      type="text" 
                      class="w-full px-3 sm:px-4 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-amber-500 rounded-xl text-xs font-medium text-gray-800 dark:text-white outline-none transition-all"
                      placeholder="0414-XXX-XXXX"
                    />
                  </div>
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                  <div>
                    <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">
                      <Icon icon="mdi:state-machine" class="inline mr-1" /> Estado del Equipo
                    </label>
                    <select v-model="kardexForm.estadoEquipo" class="w-full px-3 sm:px-4 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-amber-500 rounded-xl text-xs font-black text-gray-800 dark:text-white outline-none transition-all appearance-none cursor-pointer">
                      <option value="">Seleccionar estado...</option>
                      <option value="venta">Venta</option>
                      <option value="repuesto">Repuesto</option>
                      <option value="abandonado_por_cliente">Abandonado por Cliente</option>
                      <option value="para_reparacion">Para Reparación</option>
                    </select>
                  </div>
                  <div style="display: none;">
                    <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">
                      <Icon icon="mdi:login" class="inline mr-1" /> Tipo de Ingreso
                    </label>
                    <select v-model="kardexForm.tipoIngreso" class="w-full px-3 sm:px-4 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-amber-500 rounded-xl text-xs font-black text-gray-800 dark:text-white outline-none transition-all appearance-none cursor-pointer">
                      <option value="">Seleccionar tipo...</option>
                      <option value="reparacion">Reparación</option>
                      <option value="venta">Venta</option>
                      <option value="garantia">Garantía</option>
                      <option value="compra">Compra</option>
                      <option value="donacion">Donación</option>
                    </select>
                  </div>
                </div>
                <div class="mt-3">
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">
                    <Icon icon="mdi:text-box" class="inline mr-1" /> Que tiene el equipo
                  </label>
                  <textarea 
                    v-model="kardexForm.descripcionEquipo" 
                    rows="2"
                    class="w-full px-3 sm:px-4 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-amber-500 rounded-xl text-xs font-medium text-gray-800 dark:text-white outline-none transition-all resize-none"
                    placeholder="Descripción de lo que tiene el equipo..."
                  />
                </div>
                <div class="mt-3">
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">
                    <Icon icon="mdi:message-text" class="inline mr-1" /> Observaciones Extra
                  </label>
                  <textarea 
                    v-model="kardexForm.observacionesExtra" 
                    rows="2"
                    class="w-full px-3 sm:px-4 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-amber-500 rounded-xl text-xs font-medium text-gray-800 dark:text-white outline-none transition-all resize-none"
                    placeholder="Observaciones adicionales..."
                  />
                </div>
              </div>

              <p class="text-[8px] text-gray-400 italic">* El stock inicial se registrará como entrada en el Kardex</p>

              <div class="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button type="button" @click="showModal = false" class="w-full sm:flex-1 py-3 border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-xl font-black text-xs tracking-widest hover:bg-gray-50 dark:hover:bg-gray-700 transition-all">Cancelar</button>
                <button type="submit" :disabled="guardando" class="w-full sm:flex-[2] py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:opacity-90 transition-all shadow-lg shadow-indigo-500/30 disabled:opacity-50 flex items-center justify-center gap-2">
                  <Icon :icon="guardando ? 'mdi:loading' : 'mdi:content-save'" :class="{'animate-spin': guardando}" />
                  {{ guardando ? 'Guardando...' : (isEditing ? 'Guardar Cambios' : 'Registrar Kardex') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ============================================ -->
    <!-- MODAL DE INFORMACIÓN -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="showInfoModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]" @click.self="showInfoModal = false">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden animate-zoomIn">
          <div class="bg-gradient-to-r from-amber-600 to-orange-600 px-4 sm:px-6 py-4 flex items-center gap-3 sticky top-0 z-10">
            <div class="p-2 bg-white/20 rounded-xl">
              <Icon icon="mdi:information-outline" class="text-white text-xl" />
            </div>
            <div>
              <h3 class="text-sm font-black text-white">Información de la Herramienta</h3>
              <p class="text-[10px] text-white/70 font-bold">{{ infoHerramienta?.codigo || '' }}</p>
            </div>
            <button @click="showInfoModal = false" class="ml-auto p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all">
              <Icon icon="mdi:close" class="text-white text-lg" />
            </button>
          </div>
          <div v-if="infoHerramienta" class="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-80px)] space-y-4">
            <!-- Datos de la Herramienta -->
            <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
              <h4 class="text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                <Icon icon="mdi:tools" class="text-indigo-500" />
                Datos de la Herramienta
              </h4>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><span class="text-[10px] font-black text-gray-400 uppercase">Código</span><p class="font-bold text-gray-900 dark:text-white text-sm">{{ infoHerramienta.codigo }}</p></div>
                <div><span class="text-[10px] font-black text-gray-400 uppercase">Nombre</span><p class="font-bold text-gray-900 dark:text-white text-sm">{{ infoHerramienta.nombre }}</p></div>
                <div class="sm:col-span-2"><span class="text-[10px] font-black text-gray-400 uppercase">Descripción</span><p class="text-gray-700 dark:text-gray-300 text-sm">{{ infoHerramienta.descripcion || 'Sin descripción' }}</p></div>
                <div><span class="text-[10px] font-black text-gray-400 uppercase">Sede</span><p class="font-bold text-gray-900 dark:text-white text-sm">{{ infoHerramienta.nombreSede || '-' }}</p></div>
                <div><span class="text-[10px] font-black text-gray-400 uppercase">Categoría</span><p class="font-bold text-gray-900 dark:text-white text-sm">{{ infoHerramienta.nombreCategoria || '-' }}</p></div>
                <div><span class="text-[10px] font-black text-gray-400 uppercase">Marca</span><p class="font-bold text-gray-900 dark:text-white text-sm">{{ infoHerramienta.marca || '-' }}</p></div>
                <div><span class="text-[10px] font-black text-gray-400 uppercase">Stock</span><p :class="['font-bold text-sm', infoHerramienta.stock > 0 ? 'text-gray-900 dark:text-white' : 'text-rose-500']">{{ infoHerramienta.stock }}</p></div>
                <div><span class="text-[10px] font-black text-gray-400 uppercase">Estado</span><p class="font-bold"><span :class="['px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest border shadow-sm inline-block', infoHerramienta.estado === 'Activo' ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800' : infoHerramienta.estado === 'Mantenimiento' ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800' : 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800']">{{ infoHerramienta.estado }}</span></p></div>
              </div>
            </div>

            <!-- Ubicación -->
            <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
              <h4 class="text-[10px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                <Icon icon="mdi:map-marker" class="text-emerald-500" />
                Ubicación y Procedencia
              </h4>
              <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div><span class="text-[10px] font-black text-gray-400 uppercase">Estante</span><p class="font-bold text-gray-900 dark:text-white text-sm">{{ infoHerramienta.estante || '-' }}</p></div>
                <div><span class="text-[10px] font-black text-gray-400 uppercase">Pendaño</span><p class="font-bold text-gray-900 dark:text-white text-sm">{{ infoHerramienta.pendano || '-' }}</p></div>
                <div><span class="text-[10px] font-black text-gray-400 uppercase">Dueño Anterior</span><p class="font-bold text-gray-900 dark:text-white text-sm">{{ infoHerramienta.dueñoAnterior || '-' }}</p></div>
              </div>
            </div>

            <!-- Datos del Equipo -->
            <div class="bg-amber-50 dark:bg-amber-900/10 rounded-xl p-4 border border-amber-200 dark:border-amber-800/30">
              <h4 class="text-[10px] font-black text-amber-600 dark:text-amber-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                <Icon icon="mdi:information" class="text-amber-500" />
                Datos del Equipo
              </h4>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><span class="text-[10px] font-black text-gray-400 uppercase">Fecha de Ingreso</span><p class="font-bold text-gray-900 dark:text-white text-sm">{{ infoKardex?.fechaIngresoManual ? formatDate(infoKardex.fechaIngresoManual) : '-' }}</p></div>
                <div><span class="text-[10px] font-black text-gray-400 uppercase">Teléfono del Cliente</span><p class="font-bold text-gray-900 dark:text-white text-sm">{{ infoKardex?.telefonoCliente || '-' }}</p></div>
                <div><span class="text-[10px] font-black text-gray-400 uppercase">Estado del Equipo</span><p class="font-bold text-gray-900 dark:text-white text-sm">{{ infoKardex?.estadoEquipo ? infoKardex.estadoEquipo.replace(/_/g, ' ').toUpperCase() : '-' }}</p></div>
                <div><span class="text-[10px] font-black text-gray-400 uppercase">Que tiene el equipo</span><p class="font-bold text-gray-900 dark:text-white text-sm">{{ infoKardex?.descripcionEquipo || '-' }}</p></div>
                <div class="sm:col-span-2"><span class="text-[10px] font-black text-gray-400 uppercase">Observaciones Extra</span><p class="text-gray-700 dark:text-gray-300 text-sm">{{ infoKardex?.observacionesExtra || '-' }}</p></div>
              </div>
            </div>

            <button @click="showInfoModal = false" class="w-full py-3 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-xl font-black text-xs tracking-widest hover:bg-gray-200 dark:hover:bg-gray-600 transition-all">Cerrar</button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue';
import { Icon } from '@iconify/vue';
import { getHerramientas, createHerramienta, updateHerramienta, deleteHerramienta } from '@/api/herramientas';
import { obtenerKardex, crearKardex, eliminarKardex } from '@/api/kardexHerramienta';
import { obtenerSedes } from '@/api/sede';
import { obtenerCategorias } from '@/api/categorias';
import Swal from 'sweetalert2';

// State
const tabActivo = ref('herramientas');
const todasLasHerramientas = ref([]);
const kardex = ref([]);
const sedesList = ref([]);
const categoriasList = ref([]);
const searchQuery = ref('');
const filtroEstado = ref('');
const kardexSearchQuery = ref('');
const kardexFilterTipo = ref('all');
const showModal = ref(false);
const showInfoModal = ref(false);
const isEditing = ref(false);
const guardando = ref(false);
const cargando = ref(false);
const cargandoKardex = ref(false);
const infoHerramienta = ref(null);
const infoKardex = ref(null);

// Paginación
const currentPage = ref(1);
const kardexCurrentPage = ref(1);
const itemsPerPage = 5;
const maxVisiblePages = 10;

// Computed - Herramientas con Kardex
const herramientasConKardex = computed(() => {
  const idsConKardex = new Set(kardex.value.map(k => k.idHerramienta));
  return todasLasHerramientas.value.filter(h => idsConKardex.has(h.idHerramienta));
});

const filteredHerramientas = computed(() => {
  let result = herramientasConKardex.value;
  
  if (filtroEstado.value) {
    result = result.filter(h => h.estado === filtroEstado.value);
  }
  
  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase();
    result = result.filter(h => 
      h.nombre?.toLowerCase().includes(q) ||
      h.codigo?.toLowerCase().includes(q) ||
      (h.marca && h.marca.toLowerCase().includes(q)) ||
      (h.nombreSede && h.nombreSede.toLowerCase().includes(q))
    );
  }
  
  return result;
});

const herramientasActivas = computed(() => 
  herramientasConKardex.value.filter(h => h.estado === 'Activo').length
);

const herramientasMantenimiento = computed(() => 
  herramientasConKardex.value.filter(h => h.estado === 'Mantenimiento').length
);

const stockTotal = computed(() => 
  herramientasConKardex.value.reduce((sum, h) => sum + (h.stock || 0), 0)
);

const totalPages = computed(() => Math.ceil(filteredHerramientas.value.length / itemsPerPage));
const paginatedHerramientas = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage;
  return filteredHerramientas.value.slice(start, start + itemsPerPage);
});

const displayedPages = computed(() => {
  const total = totalPages.value;
  const max = maxVisiblePages;
  const current = currentPage.value;
  
  if (total <= max) return Array.from({ length: total }, (_, i) => i + 1);
  
  let start = Math.max(1, current - Math.floor(max / 2));
  let end = start + max - 1;
  if (end > total) { end = total; start = Math.max(1, end - max + 1); }
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
});

// Computed - Kardex
const filteredKardex = computed(() => {
  return kardex.value.filter(item => {
    const matchesSearch = 
      (item.nombreHerramienta || '').toLowerCase().includes(kardexSearchQuery.value.toLowerCase()) ||
      (item.motivo || '').toLowerCase().includes(kardexSearchQuery.value.toLowerCase());
    const matchesTipo = kardexFilterTipo.value === 'all' || item.tipoMovimiento === kardexFilterTipo.value;
    return matchesSearch && matchesTipo;
  });
});

const totalKardexEntradas = computed(() => 
  kardex.value.filter(item => item.tipoMovimiento?.toLowerCase() === 'entrada')
    .reduce((sum, item) => sum + item.cantidad, 0)
);

const totalKardexSalidas = computed(() => 
  kardex.value.filter(item => item.tipoMovimiento?.toLowerCase() === 'salida')
    .reduce((sum, item) => sum + item.cantidad, 0)
);

const herramientasUnicasKardex = computed(() => {
  const ids = new Set(kardex.value.map(item => item.idHerramienta));
  return ids.size;
});

const kardexTotalPages = computed(() => Math.ceil(filteredKardex.value.length / itemsPerPage));
const paginatedKardex = computed(() => {
  const start = (kardexCurrentPage.value - 1) * itemsPerPage;
  return filteredKardex.value.slice(start, start + itemsPerPage);
});

const kardexDisplayedPages = computed(() => {
  const total = kardexTotalPages.value;
  const max = maxVisiblePages;
  const current = kardexCurrentPage.value;
  
  if (total <= max) return Array.from({ length: total }, (_, i) => i + 1);
  
  let start = Math.max(1, current - Math.floor(max / 2));
  let end = start + max - 1;
  if (end > total) { end = total; start = Math.max(1, end - max + 1); }
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
});

// Formularios
const form = reactive({
  idHerramienta: null, codigo: '', nombre: '', descripcion: '', marca: '', stock: 0, estado: 'Activo', idSede: '', idCategoria: ''
});

const kardexForm = reactive({
  estante: '', pendano: '', dueñoAnterior: '', fechaIngresoManual: '', telefonoCliente: '', estadoEquipo: '', tipoIngreso: '', descripcionEquipo: '', observacionesExtra: ''
});

// Methods
const goToFirstPage = () => { currentPage.value = 1; };
const goToLastPage = () => { currentPage.value = totalPages.value; };

const fetchHerramientasConKardex = async () => {
  cargando.value = true;
  try {
    const response = await getHerramientas();
    todasLasHerramientas.value = Array.isArray(response) ? response : (response?.data || []);
    
    const kardexResponse = await obtenerKardex();
    if (kardexResponse.success) {
      kardex.value = kardexResponse.data || [];
      
      const herramientaMap = new Map();
      kardex.value.forEach(k => {
        if (k.idHerramienta) {
          herramientaMap.set(k.idHerramienta, {
            idSede: k.idSede, nombreSede: k.nombreSede,
            idCategoria: k.idCategoria, nombreCategoria: k.nombreCategoria,
            detalleAdicional: k.detalleAdicional,
            fechaIngresoManual: k.fechaIngresoManual,
            telefonoCliente: k.telefonoCliente,
            estadoEquipo: k.estadoEquipo,
            tipoIngreso: k.tipoIngreso,
            descripcionEquipo: k.descripcionEquipo,
            observacionesExtra: k.observacionesExtra
          });
        }
      });
      
      todasLasHerramientas.value = todasLasHerramientas.value.map(h => {
        const info = herramientaMap.get(h.idHerramienta);
        if (info) {
          let estante = '-', pendano = '-', dueñoAnterior = '-';
          if (info.detalleAdicional) {
            const estanteMatch = info.detalleAdicional.match(/Estante:\s*([^|]+)/);
            const pendanoMatch = info.detalleAdicional.match(/Pendaño:\s*([^|]+)/);
            const dueñoMatch = info.detalleAdicional.match(/Dueño:\s*([^|]+)/);
            estante = estanteMatch ? estanteMatch[1].trim() : '-';
            pendano = pendanoMatch ? pendanoMatch[1].trim() : '-';
            dueñoAnterior = dueñoMatch ? dueñoMatch[1].trim() : '-';
          }
          return { ...h, ...info, estante, pendano, dueñoAnterior };
        }
        return { ...h, idSede: null, nombreSede: '-', idCategoria: null, nombreCategoria: '-', estante: '-', pendano: '-', dueñoAnterior: '-' };
      });
    }
  } catch (error) {
    console.error('Error al cargar:', error);
    toast('Error al cargar datos', 'error');
  } finally {
    cargando.value = false;
  }
};

const fetchKardexData = async () => {
  cargandoKardex.value = true;
  try {
    const response = await obtenerKardex();
    if (response.success) kardex.value = response.data || [];
  } catch (error) {
    console.error('Error al cargar Kardex:', error);
    toast('Error al cargar datos', 'error');
  } finally {
    cargandoKardex.value = false;
  }
};

const fetchSedes = async () => {
  try {
    const response = await obtenerSedes();
    sedesList.value = response.success ? (response.data || []) : [];
  } catch (error) { console.error('Error al cargar sedes:', error); }
};

const fetchCategorias = async () => {
  try {
    const response = await obtenerCategorias();
    categoriasList.value = response.success ? (response.data || []) : [];
  } catch (error) { console.error('Error al cargar categorías:', error); }
};

const openCreateModal = () => {
  isEditing.value = false;
  Object.assign(form, { idHerramienta: null, codigo: '', nombre: '', descripcion: '', marca: '', stock: 0, estado: 'Activo', idSede: '', idCategoria: '' });
  Object.assign(kardexForm, { estante: '', pendano: '', dueñoAnterior: '', fechaIngresoManual: '', telefonoCliente: '', estadoEquipo: '', tipoIngreso: '', descripcionEquipo: '', observacionesExtra: '' });
  showModal.value = true;
};

const openEditModal = (h) => {
  isEditing.value = true;
  Object.assign(form, { ...h, idSede: h.idSede || '', idCategoria: h.idCategoria || '' });
  
  const kardexItem = kardex.value.find(k => k.idHerramienta === h.idHerramienta);
  if (kardexItem) {
    Object.assign(kardexForm, {
      fechaIngresoManual: kardexItem.fechaIngresoManual || '',
      telefonoCliente: kardexItem.telefonoCliente || '',
      estadoEquipo: kardexItem.estadoEquipo || '',
      tipoIngreso: kardexItem.tipoIngreso || '',
      descripcionEquipo: kardexItem.descripcionEquipo || '',
      observacionesExtra: kardexItem.observacionesExtra || ''
    });
  }
  
  if (h.detalleAdicional) {
    const estanteMatch = h.detalleAdicional.match(/Estante:\s*([^|]+)/);
    const pendanoMatch = h.detalleAdicional.match(/Pendaño:\s*([^|]+)/);
    const dueñoMatch = h.detalleAdicional.match(/Dueño:\s*([^|]+)/);
    kardexForm.estante = estanteMatch ? estanteMatch[1].trim() : '';
    kardexForm.pendano = pendanoMatch ? pendanoMatch[1].trim() : '';
    kardexForm.dueñoAnterior = dueñoMatch ? dueñoMatch[1].trim() : '';
  }
  showModal.value = true;
};

const openInfoModal = (h) => {
  infoHerramienta.value = h;
  infoKardex.value = kardex.value.find(k => k.idHerramienta === h.idHerramienta) || null;
  showInfoModal.value = true;
};

const saveHerramienta = async () => {
  guardando.value = true;
  try {
    const detalleAdicional = `Estante: ${kardexForm.estante || 'N/A'} | Pendaño: ${kardexForm.pendano || 'N/A'} | Dueño: ${kardexForm.dueñoAnterior || 'N/A'}`;
    let herramientaId;

    if (isEditing.value) {
      await updateHerramienta(form.idHerramienta, { ...form, detalleAdicional });
      toast('Kardex actualizado con éxito');
    } else {
      const response = await createHerramienta(form);
      herramientaId = response.idHerramienta || response.data?.idHerramienta || response.id;
      
      if (herramientaId) {
        await crearKardex({
          idHerramienta: herramientaId,
          tipoMovimiento: form.stock > 0 ? 'entrada' : 'registro',
          cantidad: form.stock > 0 ? form.stock : 1,
          stockAnterior: 0,
          stockNuevo: form.stock > 0 ? form.stock : 0,
          motivo: form.stock > 0 ? 'Registro inicial de herramienta' : 'Registro de herramienta sin stock',
          idSede: form.idSede || null,
          idCategoria: form.idCategoria || null,
          detalleAdicional,
          ubicacionEspecifica: `${kardexForm.estante || 'N/A'} - ${kardexForm.pendano || 'N/A'}`,
          idUsuarioRegistro: 1,
          referenciaTipo: 'creacion_herramienta',
          referenciaId: herramientaId,
          fechaIngresoManual: kardexForm.fechaIngresoManual || null,
          telefonoCliente: kardexForm.telefonoCliente || null,
          estadoEquipo: kardexForm.estadoEquipo || null,
          tipoIngreso: kardexForm.tipoIngreso || null,
          descripcionEquipo: kardexForm.descripcionEquipo || null,
          observacionesExtra: kardexForm.observacionesExtra || null
        });
      }
      toast('Kardex registrado con éxito');
    }

    showModal.value = false;
    await fetchHerramientasConKardex();
    await fetchKardexData();
  } catch (error) {
    console.error('Error al guardar:', error);
    toast(error.message || 'Error al guardar', 'error');
  } finally {
    guardando.value = false;
  }
};

const confirmDeleteHerramienta = async (h) => {
  const result = await Swal.fire({
    title: '¿Estás seguro?',
    text: `Se eliminará "${h.nombre}" y todos sus movimientos.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#f43f5e',
    cancelButtonColor: '#4f46e5',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar'
  });

  if (result.isConfirmed) {
    try {
      await deleteHerramienta(h.idHerramienta);
      toast('Kardex eliminado correctamente');
      await fetchHerramientasConKardex();
      await fetchKardexData();
    } catch (error) {
      toast('Error al eliminar', 'error');
    }
  }
};

const confirmDeleteKardex = async (item) => {
  const result = await Swal.fire({
    title: '¿Estás seguro?',
    text: `Se eliminará el movimiento de "${item.nombreHerramienta}".`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#f43f5e',
    cancelButtonColor: '#4f46e5',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar'
  });

  if (result.isConfirmed) {
    try {
      const response = await eliminarKardex(item.idKardex);
      if (response.success) {
        toast('Movimiento eliminado correctamente');
        await fetchKardexData();
        await fetchHerramientasConKardex();
      }
    } catch (error) {
      toast('Error al eliminar', 'error');
    }
  }
};

const resetFilters = () => { searchQuery.value = ''; filtroEstado.value = ''; };
const resetKardexFilters = () => { kardexSearchQuery.value = ''; kardexFilterTipo.value = 'all'; };
const verKardex = (h) => { tabActivo.value = 'kardex'; kardexSearchQuery.value = h.nombre || ''; };

const getTipoKardexClass = (tipo) => {
  switch (tipo?.toLowerCase()) {
    case 'entrada': return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800';
    case 'salida': return 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-300 dark:border-rose-800';
    case 'ajuste': return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800';
    case 'devolucion': return 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800';
    default: return 'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-900/20 dark:text-gray-300 dark:border-gray-800';
  }
};

const getCantidadKardexClass = (tipo) => {
  switch (tipo?.toLowerCase()) {
    case 'entrada':
    case 'devolucion': return 'text-emerald-600 dark:text-emerald-400';
    case 'salida': return 'text-rose-600 dark:text-rose-400';
    default: return 'text-gray-600 dark:text-gray-400';
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('es-VE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const toast = (message, icon = 'success') => {
  Swal.fire({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    icon,
    title: message,
    customClass: { popup: 'rounded-2xl' }
  });
};

onMounted(async () => {
  await Promise.all([fetchSedes(), fetchCategorias(), fetchHerramientasConKardex(), fetchKardexData()]);
});
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }
.custom-scrollbar::-webkit-scrollbar { height: 6px; width: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
.dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #334155; }

@keyframes zoomIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}
.animate-zoomIn { animation: zoomIn 0.2s ease-out; }

@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(15px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fadeInUp { animation: fadeInUp 0.4s ease-out forwards; }
.delay-100 { animation-delay: 0.1s; }
.delay-200 { animation-delay: 0.2s; }
.delay-300 { animation-delay: 0.3s; }

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }
</style>